import { useState, useCallback, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { AxiosResponse } from 'axios'
import { axiosInstance } from '../helpers/axios'
import {
    CONVERSATION_PROBLEMS_BY_IDS_KEY,
    useConversationProblemsByIds,
} from './useConversationProblemsByIds'
import {
    AnalyticsSummary,
    AnalysisFilters,
    AnalysisView,
    ConversationAnalyticsItem,
    ConversationAnalyticsResponse,
    DrillStep,
    DEFAULT_FILTERS,
    AnalysisFilterOption,
    Intent,
    ProblemFilters,
    DEFAULT_PROBLEM_FILTERS,
    ProblemsSummary,
    ConversationProblemsResponse,
    ConversationProblem,
    ProblemCategory,
} from '../components/AdminPanel/Lori Manager/analysisTypes'

const LORI_SUMMARY_KEY = 'loriConversationSummary'
const LORI_CONVERSATIONS_KEY = 'loriConversations'
const PROBLEMS_SUMMARY_KEY = 'loriProblemsSummary'
const PROBLEMS_LIST_KEY = 'loriProblemsList'
const PROBLEMS_BASE_PATH = '/chat/conversation-problems/'

function buildSummaryParams(filters: AnalysisFilters) {
    return {
        organization_id: filters.organizationId ?? undefined,
        workspace_id: filters.workspaceId ?? undefined,
        user_id: filters.userId ?? undefined,
        exclude_ezos_users: filters.excludeEzosUsers || undefined,
        require_user_messages: filters.requireUserMessages || undefined,
        topic_id: filters.topic ?? undefined,
        intent: filters.intent ?? undefined,
        conversation_type: filters.conversationType ?? undefined,
        start_date: filters.startDate ?? undefined,
        end_date: filters.endDate ?? undefined,
        group_by_time: filters.groupByTime,
    }
}

function buildListParams(
    filters: AnalysisFilters,
    page: number,
    pageSize: number,
    orderBy: string,
    order: 'asc' | 'desc',
) {
    return {
        ...buildSummaryParams(filters),
        page,
        page_size: pageSize,
        order_by: orderBy,
        order,
    }
}

function buildProblemParams(filters: ProblemFilters) {
    return {
        category: filters.category ?? undefined,
        start_date: filters.startDate ?? undefined,
        end_date: filters.endDate ?? undefined,
        group_by_time: filters.groupByTime,
    }
}

export interface ProblemListParams {
    page: number
    pageSize: number
}

export interface ConversationListParams {
    page: number
    pageSize: number
    orderBy: string
    order: 'asc' | 'desc'
}

export const useLoriConversationAnalysis = () => {
    const queryClient = useQueryClient()

    const [filters, setFilters] = useState<AnalysisFilters>({
        ...DEFAULT_FILTERS,
    })
    const [drillPath, setDrillPath] = useState<DrillStep[]>([])
    const [view, setView] = useState<AnalysisView>('dashboard')
    const [listParams, setListParams] = useState<ConversationListParams>({
        page: 1,
        pageSize: 50,
        orderBy: 'created_date',
        order: 'desc',
    })
    const [selectedConversationId, setSelectedConversationId] = useState<
        number | null
    >(null)

    // Problem-specific state
    const [problemFilters, setProblemFilters] = useState<ProblemFilters>({
        ...DEFAULT_PROBLEM_FILTERS,
    })
    const [
        problemListParams,
        setProblemListParams,
    ] = useState<ProblemListParams>({
        page: 1,
        pageSize: 50,
    })

    // --- Existing analytics queries ---

    const summaryQuery = useQuery<AxiosResponse<AnalyticsSummary>>({
        queryKey: [LORI_SUMMARY_KEY, buildSummaryParams(filters)],
        queryFn: () =>
            axiosInstance.get('/chat/conversation-analytics/summary/', {
                params: buildSummaryParams(filters),
            }),
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const conversationsQuery = useQuery<
        AxiosResponse<ConversationAnalyticsResponse>
    >({
        queryKey: [
            LORI_CONVERSATIONS_KEY,
            buildListParams(
                filters,
                listParams.page,
                listParams.pageSize,
                listParams.orderBy,
                listParams.order,
            ),
        ],
        queryFn: () =>
            axiosInstance.get('/chat/conversation-analytics/', {
                params: buildListParams(
                    filters,
                    listParams.page,
                    listParams.pageSize,
                    listParams.orderBy,
                    listParams.order,
                ),
            }),
        enabled: view === 'conversations',
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const organizations: AnalysisFilterOption[] = useMemo(
        () =>
            (summaryQuery.data?.data?.by_organization ?? []).map((o) => ({
                id: o.organization_id,
                name: o.organization_name,
            })),
        [summaryQuery.data?.data?.by_organization],
    )

    const workspaces: AnalysisFilterOption[] = useMemo(
        () =>
            (summaryQuery.data?.data?.by_workspace ?? []).map((w) => ({
                id: w.workspace_id,
                name: w.workspace_name,
            })),
        [summaryQuery.data?.data?.by_workspace],
    )

    const users: AnalysisFilterOption[] = useMemo(
        () =>
            (summaryQuery.data?.data?.by_user ?? []).map((u) => ({
                id: u.user_id,
                name: u.user_name,
            })),
        [summaryQuery.data?.data?.by_user],
    )

    // --- Problem queries ---

    const problemsSummaryQuery = useQuery<AxiosResponse<ProblemsSummary>>({
        queryKey: [PROBLEMS_SUMMARY_KEY, buildProblemParams(problemFilters)],
        queryFn: () =>
            axiosInstance.get(`${PROBLEMS_BASE_PATH}summary/`, {
                params: buildProblemParams(problemFilters),
            }),
        enabled: view === 'problems',
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const problemsListQuery = useQuery<
        AxiosResponse<ConversationProblemsResponse>
    >({
        queryKey: [
            PROBLEMS_LIST_KEY,
            buildProblemParams(problemFilters),
            problemListParams.page,
            problemListParams.pageSize,
        ],
        queryFn: () =>
            axiosInstance.get(PROBLEMS_BASE_PATH, {
                params: {
                    ...buildProblemParams(problemFilters),
                    page: problemListParams.page,
                    page_size: problemListParams.pageSize,
                },
            }),
        enabled: view === 'problems',
        staleTime: 2 * 60 * 1000,
        refetchOnWindowFocus: false,
        keepPreviousData: true,
    })

    const visibleConversationIds = useMemo(
        () => conversationsQuery.data?.data?.results.map((c) => c.id) ?? [],
        [conversationsQuery.data?.data?.results],
    )

    const {
        problemsByConversationId,
        isLoading: problemsByConversationIdLoading,
    } = useConversationProblemsByIds(
        visibleConversationIds,
        view === 'conversations' && visibleConversationIds.length > 0,
    )

    const conversationProblem: ConversationProblem | null = useMemo(
        () =>
            selectedConversationId !== null
                ? problemsByConversationId.get(selectedConversationId) ?? null
                : null,
        [problemsByConversationId, selectedConversationId],
    )

    // --- Problem mutations ---

    const invalidateProblemQueries = useCallback(() => {
        queryClient.invalidateQueries([PROBLEMS_SUMMARY_KEY])
        queryClient.invalidateQueries([PROBLEMS_LIST_KEY])
        queryClient.invalidateQueries([CONVERSATION_PROBLEMS_BY_IDS_KEY])
    }, [queryClient])

    const createProblemMutation = useMutation<
        ConversationProblem,
        unknown,
        { conversation: number; category: ProblemCategory; notes?: string }
    >({
        mutationFn: async (input) => {
            const res = await axiosInstance.post<ConversationProblem>(
                PROBLEMS_BASE_PATH,
                input,
            )
            return res.data
        },
        onSuccess: invalidateProblemQueries,
    })

    const updateProblemMutation = useMutation<
        ConversationProblem,
        unknown,
        { id: number; category: ProblemCategory; notes?: string }
    >({
        mutationFn: async ({ id, ...input }) => {
            const res = await axiosInstance.patch<ConversationProblem>(
                `${PROBLEMS_BASE_PATH}${id}/`,
                input,
            )
            return res.data
        },
        onSuccess: invalidateProblemQueries,
    })

    const deleteProblemMutation = useMutation<void, unknown, number>({
        mutationFn: async (id) => {
            await axiosInstance.delete(`${PROBLEMS_BASE_PATH}${id}/`)
        },
        onSuccess: invalidateProblemQueries,
    })

    // --- Conversation classification mutation ---

    const updateConversationMutation = useMutation<
        ConversationAnalyticsItem,
        unknown,
        { id: number; topic?: number | null; intent?: string | null }
    >({
        mutationFn: async ({ id, ...input }) => {
            const res = await axiosInstance.patch<ConversationAnalyticsItem>(
                `/chat/conversation-analytics/${id}/`,
                input,
            )
            return res.data
        },
        onSuccess: () => {
            queryClient.invalidateQueries([LORI_CONVERSATIONS_KEY])
            queryClient.invalidateQueries([LORI_SUMMARY_KEY])
        },
    })

    // --- Existing callbacks ---

    const drillInto = useCallback(
        (dimension: 'topic' | 'intent', value: string, label: string) => {
            const newStep: DrillStep = { dimension, value, label }
            setDrillPath((prev) => [...prev, newStep])
            setFilters((prev) => ({
                ...prev,
                [dimension]: value,
            }))
        },
        [],
    )

    const drillBack = useCallback(
        (index: number) => {
            const newDrillPath = drillPath.slice(0, index)
            setDrillPath(newDrillPath)

            const newFilters: AnalysisFilters = {
                ...filters,
                topic: null,
                intent: null,
            }
            for (const step of newDrillPath) {
                if (step.dimension === 'topic') {
                    newFilters.topic = step.value
                } else {
                    newFilters.intent = step.value as Intent
                }
            }
            setFilters(newFilters)
            setView('dashboard')
        },
        [drillPath, filters],
    )

    const showConversations = useCallback(() => {
        setListParams((prev) => ({ ...prev, page: 1 }))
        setView('conversations')
    }, [])

    const showDashboard = useCallback(() => {
        setView('dashboard')
    }, [])

    const showProblems = useCallback(() => {
        setProblemListParams((prev) => ({ ...prev, page: 1 }))
        setView('problems')
    }, [])

    const updateFilter = useCallback((partial: Partial<AnalysisFilters>) => {
        setFilters((prev) => ({ ...prev, ...partial }))
    }, [])

    const resetFilters = useCallback(() => {
        setFilters({ ...DEFAULT_FILTERS })
        setProblemFilters({ ...DEFAULT_PROBLEM_FILTERS })
        setDrillPath([])
        setView('dashboard')
    }, [])

    const updateListParams = useCallback(
        (partial: Partial<ConversationListParams>) => {
            setListParams((prev) => ({ ...prev, ...partial }))
        },
        [],
    )

    const updateProblemFilters = useCallback(
        (partial: Partial<ProblemFilters>) => {
            setProblemFilters((prev) => ({ ...prev, ...partial }))
            setProblemListParams((prev) => ({ ...prev, page: 1 }))
        },
        [],
    )

    const updateProblemListParams = useCallback(
        (partial: Partial<ProblemListParams>) => {
            setProblemListParams((prev) => ({ ...prev, ...partial }))
        },
        [],
    )

    return {
        // Existing analytics
        filters,
        drillPath,
        view,
        summary: summaryQuery.data?.data ?? null,
        summaryLoading: summaryQuery.isLoading,
        summaryFetching: summaryQuery.isFetching,
        conversations: conversationsQuery.data?.data ?? null,
        conversationsLoading: conversationsQuery.isLoading,
        conversationsFetching: conversationsQuery.isFetching,
        organizations,
        workspaces,
        users,
        listParams,
        updateListParams,
        drillInto,
        drillBack,
        showConversations,
        showDashboard,
        updateFilter,
        resetFilters,

        // Conversation classification
        updateConversation: updateConversationMutation.mutateAsync,
        updateConversationLoading: updateConversationMutation.isLoading,

        // Problems
        problemFilters,
        problemListParams,
        problemsSummary: problemsSummaryQuery.data?.data ?? null,
        problemsSummaryLoading: problemsSummaryQuery.isLoading,
        problemsList: problemsListQuery.data?.data ?? null,
        problemsListLoading: problemsListQuery.isLoading,
        problemsByConversationId,
        problemsByConversationIdLoading,
        conversationProblem,
        selectedConversationId,
        setSelectedConversationId,
        showProblems,
        updateProblemFilters,
        updateProblemListParams,
        createProblem: createProblemMutation.mutate,
        updateProblem: updateProblemMutation.mutate,
        deleteProblem: deleteProblemMutation.mutate,
        createProblemLoading: createProblemMutation.isLoading,
        updateProblemLoading: updateProblemMutation.isLoading,
        deleteProblemLoading: deleteProblemMutation.isLoading,
    }
}

export type LoriConversationAnalysisController = ReturnType<
    typeof useLoriConversationAnalysis
>
